import type { NpcFaction, NpcHullTier } from "./types";

/**
 * Resuming an interrupted fight (16.99, Don's direction).
 *
 * Don ruled that Save & Exit stays available mid-combat -- and then asked for
 * the thing that makes that fair: *"If it is possible to keep track of combat so
 * that a player that closes their browser or exits during combat is brought
 * right back to the same state when they log back in, I would prefer that."*
 *
 * Without this, leaving mid-fight was a **free escape**. Hull and shield damage
 * already persisted (16.81), but the hostiles did not, so a captain losing badly
 * could exit, return, and find an empty Location with their damage intact and
 * the enemy gone. That is a worse exploit than a "flee" button, because nothing
 * on screen admitted it existed.
 *
 * What is captured is deliberately narrow: the fight, and where it was happening.
 *
 * **What is NOT captured, and why:**
 * - Player hull/shield -- already persisted per ship instance (16.81). Storing
 *   it twice invites the two copies to disagree.
 * - `nextFireAt` and `waveGapUntil` -- both are `performance.now()` timestamps,
 *   which reset to zero on reload. A restored absolute timestamp is not merely
 *   wrong, it is wrong in the dangerous direction: every hostile would fire
 *   instantly on load. They are rebuilt fresh instead.
 * - Ambient contacts and station garrisons -- respawned by the scene itself from
 *   real state (standing, danger tier), so persisting them would double them.
 */

export const COMBAT_SNAPSHOT_VERSION = 1;

export interface HostileSnapshot {
  id: string;
  name: string;
  faction: NpcFaction;
  hullTier: NpcHullTier;
  x: number;
  y: number;
  shield: number;
  shieldMax: number;
  hull: number;
  hullMax: number;
  dps: number;
}

export interface CombatSnapshot {
  version: number;
  /** Only restored if the captain is still in this exact place. */
  systemId: string;
  locationId: string;
  encounterId: string;
  waveIndex: number;
  hostiles: HostileSnapshot[];
  playerX: number;
  playerY: number;
}

/**
 * Is this snapshot worth restoring, here and now?
 *
 * Four independent reasons to discard one, and each has actually to be checked
 * rather than assumed:
 *
 * 1. **Wrong version** -- a future shape must not be half-read.
 * 2. **Wrong place** -- a save made in one Location cannot repopulate another.
 *    This is the load-bearing one: without it, warping away and back would
 *    resurrect a dead wave, and the fight would follow the captain around the
 *    galaxy.
 * 3. **Different encounter** -- a mission Location whose mission has since
 *    changed is not the same fight.
 * 4. **Nothing left alive** -- an empty or all-dead hostile list is a cleared
 *    encounter, and restoring it would block wave progression forever.
 */
export function canRestoreCombat(
  snapshot: CombatSnapshot | null | undefined,
  systemId: string,
  locationId: string,
  encounterId: string
): boolean {
  if (!snapshot) return false;
  if (snapshot.version !== COMBAT_SNAPSHOT_VERSION) return false;
  if (snapshot.systemId !== systemId || snapshot.locationId !== locationId) return false;
  if (snapshot.encounterId !== encounterId) return false;
  return snapshot.hostiles.some((h) => h.hull > 0);
}

/**
 * Everything still standing. A hostile at zero hull was mid-death-animation when
 * the browser closed; it does not come back for a second turn.
 */
export function survivingHostiles(snapshot: CombatSnapshot): HostileSnapshot[] {
  return snapshot.hostiles.filter((h) => h.hull > 0);
}

/**
 * Field-by-field coercion, matching `normalizeSettings`'s house style: a
 * hand-edited or half-written save should degrade to "no snapshot" rather than
 * poison the Expedition View with a NaN position or a hostile with no hull.
 *
 * Returns null on anything it cannot fully vouch for. There is no partial
 * restore -- half a fight is not a fight.
 */
export function normalizeCombatSnapshot(raw: unknown): CombatSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.version !== COMBAT_SNAPSHOT_VERSION) return null;
  if (typeof r.systemId !== "string" || typeof r.locationId !== "string" || typeof r.encounterId !== "string") {
    return null;
  }
  if (!Array.isArray(r.hostiles)) return null;
  const hostiles: HostileSnapshot[] = [];
  for (const h of r.hostiles) {
    const normalized = normalizeHostile(h);
    if (!normalized) return null;
    hostiles.push(normalized);
  }
  return {
    version: COMBAT_SNAPSHOT_VERSION,
    systemId: r.systemId,
    locationId: r.locationId,
    encounterId: r.encounterId,
    waveIndex: num(r.waveIndex, 0),
    hostiles,
    playerX: num(r.playerX, 0),
    playerY: num(r.playerY, 0)
  };
}

function normalizeHostile(raw: unknown): HostileSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const h = raw as Record<string, unknown>;
  if (typeof h.id !== "string" || typeof h.name !== "string") return null;
  if (typeof h.faction !== "string" || typeof h.hullTier !== "string") return null;
  const hullMax = num(h.hullMax, 0);
  if (hullMax <= 0) return null;
  const shieldMax = num(h.shieldMax, 0);
  return {
    id: h.id,
    name: h.name,
    faction: h.faction as NpcFaction,
    hullTier: h.hullTier as NpcHullTier,
    x: num(h.x, 0),
    y: num(h.y, 0),
    shield: clamp(num(h.shield, 0), 0, shieldMax),
    shieldMax,
    hull: clamp(num(h.hull, hullMax), 0, hullMax),
    hullMax,
    dps: Math.max(0, num(h.dps, 0))
  };
}

function num(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
